import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, UserPlus, Settings, Swords, BarChart3, History, CircleDot, Mail } from "lucide-react";

const steps = [
  {
    id: "register",
    icon: UserPlus,
    title: "Create Your Account",
    color: "#E3292E",
    items: [
      { label: "Register", desc: "Head to Admin Login and click Register" },
      { label: "Enter Venue Name", desc: "This is your default location — choose it carefully as it stays linked to your account", bold: true },
      { label: "Email", desc: "You'll be asked to verify your email before you can continue" },
      { label: "Username & Password", desc: "Pick a username and secure password" },
      { label: "Verify Email", desc: "Check your inbox, click the verification link, then log in" },
      { label: "Free Credit", desc: "First-time users receive 1 free credit to create their first event", highlight: true },
    ],
  },
  {
    id: "setup",
    icon: Settings,
    title: "Setup Tab — Create an Event",
    color: "#E3292E",
    items: [
      { label: "Create Event", desc: "Give your event a name (e.g. 'Friday Night Darts')" },
      { label: "Choose Event Type", desc: "Social (round-robin), Tournament (round-robin + divisions), or Knockout (single elimination bracket)" },
      { label: "Choose Format & Play Mode", desc: "Format (501/301/etc), Play Mode: Singles, Doubles, or Teams (2-10 players per team)" },
      { label: "Knockout Format", desc: "Best with 4, 8, 16, or 32 teams — other counts work but some get a first-round bye", highlight: true },
      { label: "Set Available Boards", desc: "This is important — it determines how many matches run at the same time and affects the entire schedule", bold: true },
      { label: "Click 'Create Event'", desc: "Your event is now in setup mode" },
      { label: "Add Players / Teams", desc: "Add from your Player Database or type new names. In Teams mode, build each team manually with the team builder" },
      { label: "Generate Draw / Bracket", desc: "Social/Tournament: randomly pairs players. Knockout: generates the elimination bracket" },
      { label: "Start Matches", desc: "Confirm your board count, then matches begin" },
    ],
  },
  {
    id: "matches",
    icon: Swords,
    title: "Matches Tab — Run the Event",
    color: "#309F6A",
    items: [
      { label: "Record Winners", desc: "Tap the WIN button next to the team/player that won. In Knockout, click the winning team name in the bracket" },
      { label: "Add Match Scores", desc: "After a win, click 'Add Scores' to record Highest Score, Lowest Score, and Highest Checkout (Close)" },
      { label: "Continue", desc: "Press Continue to confirm the result and load the next match onto that board" },
      { label: "Substitute Players", desc: "Swap a player mid-event — click the sub icon next to any team to replace a player", bold: true },
      { label: "Remove Teams/Players", desc: "Remove a team from the event. Soft-delete keeps their past stats in the standings and PDF" },
      { label: "Add Teams Mid-Event", desc: "Use the '+ Add Team' button to add new teams/players during a running event" },
      { label: "Track Progress", desc: "See match progress and Queued Matches showing what's coming up" },
      { label: "End Event", desc: "When all matches are done, the app notifies you. You can also end early at any time" },
    ],
  },
  {
    id: "stats",
    icon: BarChart3,
    title: "Stats Tab — View & Download",
    color: "#309F6A",
    items: [
      { label: "Live Standings", desc: "See the leaderboard, wins, losses, and win percentages" },
      { label: "Notable Scores", desc: "View highest scores, lowest scores, and best checkouts from the event" },
      { label: "Player Career Stats", desc: "See each player's record across all events they've played" },
      { label: "Download PDF", desc: "Click 'Download PDF Stats' to get a printable summary of the event" },
    ],
  },
  {
    id: "history",
    icon: History,
    title: "History Tab — Past Events",
    color: "#E3292E",
    items: [
      { label: "Event List", desc: "Browse all past events with dates and results" },
      { label: "View Stats", desc: "Click on any past event to see its full standings and scores" },
      { label: "Download", desc: "Download PDF stats for any previous event" },
      { label: "Delete Events", desc: "Remove test or unwanted events — permanently deletes the event and all its data" },
    ],
  },
  {
    id: "contact",
    icon: Mail,
    title: "Contact Us",
    color: "#E3292E",
    items: [
      { label: "Need Help?", desc: "Please contact us should you require assistance" },
      { label: "support@socialdarts.online", desc: "We'll get back to you as soon as possible", highlight: true },
    ],
  },
];

export function SetupGuideDialog({ open, onOpenChange }) {
  const [page, setPage] = useState(0);
  const step = steps[page];
  const Icon = step.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPage(0); onOpenChange(v); }}>
      <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
            Setup Guide
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex gap-1 px-5 pt-3 pb-4">
          {steps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setPage(i)}
              className={`h-1 flex-1 transition-colors ${i === page ? "bg-[#E3292E]" : i < page ? "bg-[#309F6A]" : "bg-white/10"}`}
              data-testid={`guide-step-indicator-${i}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-2" style={{ maxHeight: "55vh" }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: `${step.color}40`, background: `${step.color}10` }}>
              <Icon className="w-5 h-5" style={{ color: step.color }} />
            </div>
            <div>
              <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-bold">Step {page + 1} of {steps.length}</p>
              <h3 className="font-heading font-bold text-white text-lg">{step.title}</h3>
            </div>
          </div>

          <div className="space-y-0">
            {step.items.map((item, idx) => (
              <div key={idx} className="flex gap-3 py-3 border-b border-white/5 last:border-0">
                <div className="flex-shrink-0 mt-0.5">
                  <CircleDot className="w-3.5 h-3.5" style={{ color: item.highlight ? "#309F6A" : item.bold ? "#E3292E" : "rgba(255,255,255,0.2)" }} />
                </div>
                <div>
                  <p className={`text-sm font-heading font-bold ${item.bold ? "text-[#E3292E]" : item.highlight ? "text-[#309F6A]" : "text-white"}`}>
                    {item.label}
                  </p>
                  <p className="text-white/45 text-sm font-body mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-white/5">
          <Button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            variant="ghost"
            className="text-white/40 hover:text-white hover:bg-white/5 rounded-none text-sm disabled:opacity-20"
            data-testid="guide-prev-btn"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <p className="text-white/20 text-xs font-body">{page + 1} / {steps.length}</p>
          {page < steps.length - 1 ? (
            <Button
              onClick={() => setPage((p) => p + 1)}
              className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none text-sm font-heading font-bold"
              data-testid="guide-next-btn"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => { setPage(0); onOpenChange(false); }}
              className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none text-sm font-heading font-bold"
              data-testid="guide-done-btn"
            >
              Got It
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
